import { type } from "os";
import { ApiResponse, BaseCreateResponse, BaseInfo, BaseResponse, BaseResponseWithData, baseUrl, deleteWithAuth, EmptyRequest, GetParams, getWithAuth, getWithAuthAndParams, Guid, ListRespone, ListResponeWithHash, patchWithAuth, postWithAuth } from "./baseApi";
import { CampaignStatus } from "./campaingsApi";
import { MediaContentType, MediaType } from "./mediaApi";
import { SourceType } from "./priorityListApi";

export enum DayOfWeek {
    Sunday,
    Monday,
    Tuesday,
    Wednesday,
    Thursday,
    Friday,
    Saturday,
}

export enum  TestEnum { One }

export type BaseChannelModel = {
    id: Guid,
    name: string
}

export type Channel = {
    id: Guid,
    name: string,
    mediaItems: number,
    durationMs: number,
    playlistsCount: number
    playersIds: string[],
    timeUpdate: string,
    dayOfUpdate: string,
    playInRandomOrder: boolean
}

export type TimeSlotModel = {
    id: Guid,
    dayOfWeek : DayOfWeek,
    hour : number,
    minute: number
}

export type EnableSlotRequest = {
    dayOfWeek : DayOfWeek,
    hour : number,
    minute: number
}

export type EnableRangeOfTimeSlots = {
    timeslots: EnableSlotRequest[]
}

export type CreateChannelRequest = {
    name: string,
    playerIds: string[],
    timeOfUpdate?: string,
    dayOfUpdate: number | null,
}
export type CreateChannelResponse = {}

export type UpdateChannelRequest = CreateChannelRequest & {id : string};

export type UpdateChannelResponse = {};

export type ChannelPlayerModel = {
    name: string,
    location: string,
    lastSeen: string
    id: Guid,
    tags: {id: Guid, name: string}[]
}

export type ChannelMediaModel = {
    id: Guid,
    name: string,
    campaignName: string,
    mediaType: MediaType,
    order: number,
    previewUrl: string,
    durationMs: number,
    isPaused: boolean,
    isExcluded: boolean,
    campaignStatus: CampaignStatus,
    isPausedInCampaign: boolean,
    isTriggeringSkin: boolean,
    url: string,
    contentType: MediaContentType,
    tags: BaseInfo[],
    flyDates: FlyDate[],
    shareOfVoice: number,
    shareOfVoiceCopy: number,
    taxonomiesCategoryName: string
}

export type FlyDate = {
    id: Guid,
    name: string,
    start: string,
    end: string,
    startTime: string,
    endTime: string
}

export type ReorderItemPair = {
    from: Guid;
    to: Guid
}

export type ReorderChannelMediaRequest = {
  reorderItems: ReorderItemPair[]
}

export type ChannelStream = {
    id: Guid,
    name: string,
    startTime: string,
    endTime: string,
    isTriggeringSkin: boolean,
    tvChannelId: Guid | null,
    tvChannelName: string,
    rtmpUrl: string,
    sourceType: SourceType
}

export type CreateChannelStreamRequest = {
    name: string,
    startTime: string,
    endTime: string,
    tvChannelId: Guid | null,
    rtmpUrl: string,
    sourceType: SourceType
}

export interface SiteNestedPlayList {
    id: Guid,
    name: string,
    order: number,
    useRandomOrder: boolean
}

export interface CreateSiteNestedPlayListRequest {
    name: string,
    useRandomOrder: boolean
}

export interface UpdateSiteNestedPlayListRequest {
    name: string,
    useRandomOrder: boolean
}

export interface SiteNestedPlaylistForCampaign {
    playListId: Guid,
    siteName: string,
    playListName: string
}

export type UpdatePlaylistRandomOrderRequest = {
    useRandom : boolean
}

export type LocalFile = {
    id:Guid,
    name: string,
    path: string,
    startTime: string,
    endTime: string,
    subtitlesDelayMs: number
}

export type LocalFileRequest = Omit<LocalFile, "id">

export interface NestedPlaylistMediaLiveStatistic {
    scheduled: MediaLiveStatistic
    live: MediaLiveStatistic
}
  
export interface MediaLiveStatistic {
    campaignsDurationMs: number
    campaignsPercentage: number
    siteContentDurationMs: number
    siteContentPercentage: number
    inhouseContentDurationMs: number
    inhouseContentPercentage: number
    othersDurationMs: number
    othersPercentage: number
    total: number
}

export type GroupMedia = {
    id: Guid,
    name: string,
    type: number,
    singleMedia: ChannelMediaModel | null,
    groupMedia: ChannelMediaModel[] | null,
    durationScheduled: number,
    durationLive: number,
}

export type ReorderedIds = {
    ids: Guid[],
    hash: number
}

export type GroupForMediaRequest = {
    groupId: Guid | null,
    hash: number
}

export type SetSpeedLocalfileRequest = {
    speed: number
}

export type AllDaysStatistic = {
    days: DayStatistic[]
}

export type DayStatistic = {
    date: string,
    statistic: NetworkStatisticInfo,
    isFromHistory: boolean
}

export type NetworkStatisticInfo = {
    campaignsDurationMs: number,
    campaignsPercentage: number,
    siteContentDurationMs: number,
    siteContentPercentage: number,
    inhouseContentDurationMs: number,
    inhouseContentPercentage: number,
    othersDurationMs: number,
    othersPercentage: number,
    total: number
}

export type StaisticRequest = {
    startDateString: string,
    endDateString: string,
    hour: string,
    isLive: boolean,
    tagIds: Guid[]
}

export type HistoricStaisticRequest = {
    dateTimeString: string,
    isLive: boolean,
    tagIds: Guid[]
}

export type HistoricView = {
    media: HistoricMedia[],
    forDateTime: string,
    isHistorian: boolean,
    statistic: NetworkStatisticInfo,
}

export type HistoricMedia = {
    name: string,
    url:string,
    durationMs: number ,
    mediaType: MediaType
}

export type DuplicateMediaInGroupsRequest = {
    groupIds: Guid[],
    hash: number,
    order: number | null
}

export const createChannel = async(request : CreateChannelRequest) : Promise<ApiResponse<CreateChannelResponse>> => {
    return await postWithAuth<CreateChannelRequest, CreateChannelResponse>(`${baseUrl}channels`, request);
}

export const updateChannel = async(request : UpdateChannelRequest) : Promise<ApiResponse<UpdateChannelResponse>> => {
    return await patchWithAuth<UpdateChannelRequest, UpdateChannelResponse>(`${baseUrl}channels`, request);
}

export const getChannelById = async(id: Guid) : Promise<ApiResponse<Channel>> => {
    return await getWithAuth<Channel>({url: `${baseUrl}channels/${id}`});
}

export const getChannels = async (campaignId?: Guid) : Promise<ApiResponse<ListRespone<Channel>>> => {
    const params = new GetParams();
    params.Add("full", "true");
    if(campaignId) {
        params.Add("campaignId", campaignId);
    }
    return await getWithAuthAndParams<ListRespone<Channel>>(`${baseUrl}channels`, params); 
}

export const getBaseChannels = async () : Promise<ApiResponse<ListRespone<BaseChannelModel>>> => {
    return await getWithAuth<ListRespone<BaseChannelModel>>({url: `${baseUrl}channels`}); 
}

export const getChannelsShort = async () : Promise<ApiResponse<ListRespone<BaseChannelModel>>> => {
    return await getWithAuth<ListRespone<BaseChannelModel>>({url: `${baseUrl}channels/short`}); 
}

export const getBaseChannelsForPriorityList = async () : Promise<ApiResponse<ListRespone<BaseChannelModel>>> => {
    const params = new GetParams();
    params.Add("onlyWithoutScheduledPriorityList", "true");
    return await getWithAuthAndParams<ListRespone<BaseChannelModel>>(`${baseUrl}channels`, params); 
}

export const getChannelTimeSlots = async (id: Guid) : Promise<ApiResponse<ListRespone<TimeSlotModel>>> => {
    return await getWithAuth<ListRespone<TimeSlotModel>>({url: `${baseUrl}channels/${id}/timeslots`}); 
}

export const getChannelPlayers = async (id: Guid) : Promise<ApiResponse<ListRespone<ChannelPlayerModel>>> => {
    return await getWithAuth<ListRespone<ChannelPlayerModel>>({url: `${baseUrl}channels/${id}/players`}); 
}

export const enableRangeOfTimeSlots = async (timeSlots: EnableRangeOfTimeSlots, channelId: Guid) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<EnableRangeOfTimeSlots, BaseCreateResponse>(`${baseUrl}channels/${channelId}/timeslots/bulk`, timeSlots); 
}

export const enableTimeSlots = async (timeSlot: EnableSlotRequest, channelId: Guid) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<EnableSlotRequest, BaseCreateResponse>(`${baseUrl}channels/${channelId}/timeslots`, timeSlot); 
}

export const removeTimeSlots = async (channelId: Guid, timeSlotId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await deleteWithAuth<BaseResponse>(`${baseUrl}channels/${channelId}/timeslots`, timeSlotId); 
}

export const enableSkinChannelStream = async (channelId: Guid, streamId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}channels/${channelId}/streams/${streamId}/enable-skin`, {}); 
}

export const disableSkinChannelStream = async (channelId: Guid, streamId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}channels/${channelId}/streams/${streamId}/disable-skin`, {}); 
}


//stream
export const getChannelStreams = async (channelId: Guid) : Promise<ApiResponse<ListRespone<ChannelStream>>> => {
    return await getWithAuth<ListRespone<ChannelStream>>({url: `${baseUrl}channels/${channelId}/streams`}); 
}

export const createChannelStream = async (request: CreateChannelStreamRequest, channelId: Guid) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<CreateChannelStreamRequest, BaseCreateResponse>(`${baseUrl}channels/${channelId}/streams`, request); 
}

export const updateChannelStream = async (request: CreateChannelStreamRequest, streamId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<CreateChannelStreamRequest, BaseResponse>(`${baseUrl}channels/streams/${streamId}`, request); 
}

export const deleteChannelStream = async (streamId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await deleteWithAuth<BaseCreateResponse>(`${baseUrl}channels/streams`, streamId); 
}

export const deleteChannel = async (channelId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await deleteWithAuth<BaseResponse>(`${baseUrl}channels`, channelId); 
}


// ADD NESTED PLAYLIST
export const getSiteNestedPlayLists = async(campaignId: Guid) : Promise<ApiResponse<ListRespone<SiteNestedPlayList>>> => {
    return await getWithAuth<ListRespone<SiteNestedPlayList>>({url : `${baseUrl}channels/${campaignId}/nested-playlists`});
}

export const createSiteNestedPlayList = async(siteId : Guid, request : CreateSiteNestedPlayListRequest) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<CreateSiteNestedPlayListRequest,BaseCreateResponse>(`${baseUrl}channels/${siteId}/nested-playlists`, request);
}

export const updateSiteNestedPlayList = async(siteId : Guid, nestedPlayListId: Guid, request : UpdateSiteNestedPlayListRequest) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<UpdateSiteNestedPlayListRequest,BaseResponse>(`${baseUrl}channels/${siteId}/nested-playlists/${nestedPlayListId}`, request);
}

export const deleteSiteNestedPlayList = async (siteId : Guid, nestedPlaylistId: Guid) : Promise<ApiResponse<BaseResponse>> =>{
    return await deleteWithAuth<BaseResponse>(`${baseUrl}channels/${siteId}/nested-playlists`, nestedPlaylistId)
}

export const getSiteNestedPlayListMedias = async(siteId : Guid, nestedPlaylistId: Guid) : Promise<ApiResponse<ListRespone<ChannelMediaModel>>> => {
    return await getWithAuth<ListRespone<ChannelMediaModel>>({url : `${baseUrl}channels/${siteId}/nested-playlists/${nestedPlaylistId}/media`});
}

export const getSiteNestedPlayListMediaLiveStatistic = async(siteId : Guid, nestedPlaylistId: Guid, tagIds: Guid[]) : Promise<ApiResponse<BaseResponseWithData<NestedPlaylistMediaLiveStatistic>>> => {
    const params = new GetParams();
    tagIds.forEach( el => {
        params.Add("tagIds", el);
    })
    return await getWithAuthAndParams<BaseResponseWithData<NestedPlaylistMediaLiveStatistic>>(`${baseUrl}channels/${siteId}/nested-playlists/${nestedPlaylistId}/live-statistic`, params);
}

export const reorderNestedPlaylists = async (request: ReorderChannelMediaRequest, siteId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<ReorderChannelMediaRequest, BaseResponse>(`${baseUrl}channels/${siteId}/nested-playlists/order`, request); 
}

export const reorderNestedPlaylistMedia = async (request: ReorderChannelMediaRequest, siteId: Guid, nestedPlaylistId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<ReorderChannelMediaRequest, BaseResponse>(`${baseUrl}channels/${siteId}/nested-playlists/${nestedPlaylistId}/media/order`, request); 
}

export const playNestedPlaylistMedia = async (siteId: Guid, nestedPlaylistId: Guid, mediaId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}channels/${siteId}/nested-playlists/${nestedPlaylistId}/media/${mediaId}/play`, {}); 
}

export const pauseNestedPlaylistMedia = async (siteId: Guid, nestedPlaylistId: Guid, mediaId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}channels/${siteId}/nested-playlists/${nestedPlaylistId}/media/${mediaId}/pause`, {}); 
}

export const enableSkinNestedPlaylistMedia = async (siteId: Guid, nestedPlaylistId: Guid, mediaId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}channels/${siteId}/nested-playlists/${nestedPlaylistId}/media/${mediaId}/enable-skin`, {}); 
}

export const disableSkinNestedPlaylistMedia = async (siteId: Guid, nestedPlaylistId: Guid, mediaId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}channels/${siteId}/nested-playlists/${nestedPlaylistId}/media/${mediaId}/disable-skin`, {}); 
}

export const cloneNestedPlaylistMedia = async (siteId: Guid, nestedPlaylistId: Guid, mediaId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}channels/${siteId}/nested-playlists/${nestedPlaylistId}/media/${mediaId}/clone`, {}); 
}

export const getSiteNestedPlayListsForCampaign = async() : Promise<ApiResponse<ListRespone<SiteNestedPlaylistForCampaign>>> => {
    return await getWithAuth<ListRespone<SiteNestedPlaylistForCampaign>>({url : `${baseUrl}channels/nested-playlists/site`});
}

export const enableSitePlaylistsRandomOrder = async(siteId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}channels/${siteId}/enable-random-order`, {});
}

export const disableSitePlaylistsRandomOrder = async(siteId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}channels/${siteId}/disable-random-order`, {});
}

//Local files
export const getLocalFiles = async(siteId:Guid) : Promise<ApiResponse<ListRespone<LocalFile>>> => {
    return await getWithAuth<ListRespone<LocalFile>>({url : `${baseUrl}channels/${siteId}/localfiles`});
}

export const createLocalFile = async(siteId : Guid, request : LocalFileRequest) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<LocalFileRequest,BaseCreateResponse>(`${baseUrl}channels/${siteId}/localfiles`, request);
}

export const updateLocalFile = async(siteId : Guid, id: Guid, request : LocalFileRequest) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<LocalFileRequest,BaseResponse>(`${baseUrl}channels/${siteId}/localfiles/${id}`, request);
}

export const deleteLocalFile = async (siteId : Guid, id: Guid) : Promise<ApiResponse<BaseResponse>> =>{
    return await deleteWithAuth<BaseResponse>(`${baseUrl}channels/${siteId}/localfiles`, id)
}

export const getGroupedMedia = async (siteId: Guid, nestedPlayListsId: Guid, tagIds: Guid[]) : Promise<ApiResponse<ListResponeWithHash<GroupMedia>>> =>{
    const params = new GetParams();
    tagIds.forEach( el => {
        params.Add("tagIds", el);
    })
    return await getWithAuthAndParams<ListResponeWithHash<GroupMedia>>(`${baseUrl}channels/${siteId}/nested-playlists/${nestedPlayListsId}/grouped-media`, params)
}

export const reorderGroupMedia = async (request: ReorderedIds, siteId: Guid, nestedPlaylistId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<ReorderedIds, BaseResponse>(`${baseUrl}channels/${siteId}/nested-playlists/${nestedPlaylistId}/grouped-media/reorder`, request); 
}

export const updateGroupForMedia = async (request: GroupForMediaRequest, siteId: Guid, nestedPlaylistId: Guid, mediaId:Guid) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<GroupForMediaRequest, BaseCreateResponse>(`${baseUrl}channels/${siteId}/nested-playlists/${nestedPlaylistId}/grouped-media/${mediaId}`, request); 
}

export const getGroupsForMedia = async (siteId: Guid, nestedPlayListsId: Guid) : Promise<ApiResponse<ListRespone<{id:Guid, name:string}>>> =>{
    return await getWithAuth<ListRespone<{id:Guid, name:string}>>({url: `${baseUrl}channels/${siteId}/nested-playlists/${nestedPlayListsId}/groups`})
}

export const createGroupForMedia = async (request: {name:string}, siteId: Guid, nestedPlaylistId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<{name:string}, BaseResponse>(`${baseUrl}channels/${siteId}/nested-playlists/${nestedPlaylistId}/groups`, request); 
}

export const changeGroupName = async (request: {name:string}, groupId: Guid , siteId: Guid, nestedPlaylistId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<{name:string}, BaseResponse>(`${baseUrl}channels/${siteId}/nested-playlists/${nestedPlaylistId}/groups/${groupId}`, request); 
}

export const cloneGroupMedia = async (siteId: Guid, nestedPlaylistId: Guid, groupId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}channels/${siteId}/nested-playlists/${nestedPlaylistId}/groups/${groupId}/clone`, {}); 
}

export const removeGroupMedia = async (siteId : Guid, nestedPlaylistId: Guid, groupId: Guid) : Promise<ApiResponse<BaseResponse>> =>{
    return await deleteWithAuth<BaseResponse>(`${baseUrl}channels/${siteId}/nested-playlists/${nestedPlaylistId}/groups`, groupId)
}

export const stopLocalFile = async (siteId: Guid, fileId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}channels/${siteId}/localfiles/${fileId}/stop`, {}); 
}
export const pauseLocalFile = async (siteId: Guid, fileId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}channels/${siteId}/localfiles/${fileId}/pause`, {}); 
}
export const playLocalFile = async (siteId: Guid, fileId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}channels/${siteId}/localfiles/${fileId}/play`, {}); 
}
export const restartLocalFile = async (siteId: Guid, fileId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}channels/${siteId}/localfiles/${fileId}/restart`, {}); 
}
export const jumpToLocalFile = async (siteId: Guid, fileId: Guid, timeCode: string) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}channels/${siteId}/localfiles/${fileId}/jump`, {timeCode: timeCode}); 
}
export const setSpeedLocalFile = async (siteId: Guid, fileId: Guid, request: SetSpeedLocalfileRequest) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<SetSpeedLocalfileRequest, BaseResponse>(`${baseUrl}channels/${siteId}/localfiles/${fileId}/set-speed`, request); 
}

export const getDaysStatistic = async (siteId: Guid, nestedPlayListsId: Guid, request: StaisticRequest) : Promise<ApiResponse<BaseResponseWithData<AllDaysStatistic>>> =>{
    return await postWithAuth<StaisticRequest,BaseResponseWithData<AllDaysStatistic>>(`${baseUrl}channels/${siteId}/nested-playlists/${nestedPlayListsId}/days-statistic`, request)
}

export const getHistoricView = async (siteId: Guid, nestedPlayListsId: Guid, request: HistoricStaisticRequest) : Promise<ApiResponse<BaseResponseWithData<HistoricView>>> =>{
    return await postWithAuth<HistoricStaisticRequest,BaseResponseWithData<HistoricView>>(`${baseUrl}channels/${siteId}/nested-playlists/${nestedPlayListsId}/historic-view`, request)
}

export const shuffleMediaItems = async (siteId: Guid, nestedPlaylistId: Guid, groupId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}channels/${siteId}/nested-playlists/${nestedPlaylistId}/groups/${groupId}/shuffle`, {}); 
}

export const duplicateMediaInGroups = async (request: DuplicateMediaInGroupsRequest, siteId: Guid, nestedPlaylistId: Guid, mediaId:Guid) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<DuplicateMediaInGroupsRequest, BaseCreateResponse>(`${baseUrl}channels/${siteId}/nested-playlists/${nestedPlaylistId}/media/${mediaId}/clone-to-groups`, request); 
}

